import { lazy } from 'react';

import type { RouteConfig } from '../../types';

const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));
const CompaniesLayout = lazy(() => import('@/pages/companies/layout'));
const CompanyDetailLayout = lazy(() => import('@/pages/companies/detail/layout'));
const CompaniesListPage = lazy(() => import('@/pages/companies'));
const CompanyCreatePage = lazy(() => import('@/pages/companies/create'));
const CompanyDetailIndexPage = lazy(() => import('@/pages/companies/detail'));
const CompanyOverviewPage = lazy(() => import('@/pages/companies/detail/overview'));
const CompanyUpdatePage = lazy(() => import('@/pages/companies/detail/update'));
const CompanyHistoryPage = lazy(() => import('@/pages/companies/detail/history'));
const CompanyEmployeesPage = lazy(() => import('@/pages/companies/detail/employees'));
const CompanyClientsPage = lazy(() => import('@/pages/companies/detail/clients'));
const CompanyConnectClientsPage = lazy(() => import('@/pages/companies/detail/clients/connect'));
const CompanyContractsListPage = lazy(() => import('@/pages/companies/detail/clients/companyContracts'));
const CompanyContractCreatePage = lazy(() => import('@/pages/companies/detail/clients/companyContracts/create'));
const CompanyContractDetailLayout = lazy(
  () => import('@/pages/companies/detail/clients/companyContracts/detail/layout')
);
const CompanyContractDetailIndexPage = lazy(() => import('@/pages/companies/detail/clients/companyContracts/detail'));
const CompanyContractViewPage = lazy(() => import('@/pages/companies/detail/clients/companyContracts/detail/view'));
const CompanyContractUpdatePage = lazy(() => import('@/pages/companies/detail/clients/companyContracts/detail/update'));
const CompanyContractHistoryPage = lazy(
  () => import('@/pages/companies/detail/clients/companyContracts/detail/history')
);
const CompanyContractJobSpecificationsListPage = lazy(
  () => import('@/pages/companies/detail/clients/companyContracts/detail/jobSpecifications')
);
const CompanyJobSpecificationCreatePage = lazy(
  () => import('@/pages/companies/detail/clients/companyContracts/detail/jobSpecifications/create')
);
const CompanyJobSpecificationViewPage = lazy(
  () => import('@/pages/companies/detail/clients/companyContracts/detail/jobSpecifications/detail/view')
);
const CompanyJobSpecificationUpdatePage = lazy(
  () => import('@/pages/companies/detail/clients/companyContracts/detail/jobSpecifications/detail/update')
);

/**
 * UG-4298 — company (vendor) entity routes, GPM-style nesting
 * (gpm-webapp-v2 `router/routes/groups/companies.ts` minus COI/netTerms).
 * `CompaniesLayout` mounts the section breadcrumb; `CompanyDetailLayout` is
 * the company fetch + header + sub-nav shell around every nested resource;
 * `CompanyContractDetailLayout` the tabbed company-contract shell — including
 * the contract's job-specification (rate card) pages. Permissions:
 * `@/config/auth/routeAccess`.
 */
export const companyRoutes: RouteConfig = {
  path: '/companies',
  layout: DashboardLayout,
  children: [
    {
      path: '',
      layout: CompaniesLayout,
      children: [
        { path: '', element: CompaniesListPage },
        { path: '/create', element: CompanyCreatePage },
        {
          path: '/:companyId',
          layout: CompanyDetailLayout,
          children: [
            { path: '', element: CompanyDetailIndexPage },
            { path: '/overview', element: CompanyOverviewPage },
            { path: '/update', element: CompanyUpdatePage },

            { path: '/employees', element: CompanyEmployeesPage },

            { path: '/clients', element: CompanyClientsPage },
            { path: '/clients/connect', element: CompanyConnectClientsPage },
            { path: '/clients/:clientId/companyContracts', element: CompanyContractsListPage },
            { path: '/clients/:clientId/companyContracts/create', element: CompanyContractCreatePage },
            {
              path: '/clients/:clientId/companyContracts/:companyContractId',
              layout: CompanyContractDetailLayout,
              children: [
                { path: '', element: CompanyContractDetailIndexPage },
                { path: '/view', element: CompanyContractViewPage },
                { path: '/update', element: CompanyContractUpdatePage },
                { path: '/history', element: CompanyContractHistoryPage },
                { path: '/jobSpecifications', element: CompanyContractJobSpecificationsListPage },
                { path: '/jobSpecifications/create', element: CompanyJobSpecificationCreatePage },
                {
                  path: '/jobSpecifications/:companyJobSpecificationId/view',
                  element: CompanyJobSpecificationViewPage,
                },
                {
                  path: '/jobSpecifications/:companyJobSpecificationId/update',
                  element: CompanyJobSpecificationUpdatePage,
                },
              ],
            },

            { path: '/history', element: CompanyHistoryPage },
          ],
        },
      ],
    },
  ],
};
